import { DeckCardKakaoKind } from "@/modules/kakao";
import type { DeckCardType, PublishedCard } from "../../types";

import { DeckCardThumbnailRecord } from "../../types/app-deck/deckCardThumbnail.types";
import { isCardThumbnailPurchased } from "../../types/app-deck/deckCardThumbnail.types";
import {
  getMainCardContent,
  getMainCardRefId,
  getMainCardType,
  getPublishedCardShellContent,
  isMainCardPurchased,
  isPublishedCardPurchased,
  normalizeMainCardTypeKey,
  resolveMainCardShellPages,
} from "./mainCardShellReaders";
import { enhanceDemoCardForPreview } from "@/cards/demo-card/utils/demoCardPreviewUtils";
import { enhanceSeasonCardForPreview } from "@/cards/season-card/utils/seasonCardPreviewUtils";
import { parseSealDeadline } from "@/cards/common/utils/useSealTimeGate";

export {
  getMainCardContent,
  getMainCardRefId,
  getMainCardType,
  getPublishedCardShellContent,
  isMainCardPurchased,
  isMainCardShellType,
  isPublishedCardPurchased,
  normalizeMainCardTypeKey,
  resolveMainCardShellPages,
} from "./mainCardShellReaders";

/** Keep API `table_of_contents` on the card so TOC can show locked rows when not bought. */
const withTableOfContents = (rawCard: Record<string, unknown>) =>
  Array.isArray(rawCard.table_of_contents)
    ? { table_of_contents: rawCard.table_of_contents as PublishedCard["table_of_contents"] }
    : {};

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

export const appendSingleCardEndingCoverPage = (
  card: Record<string, unknown>,
  pages: Record<string, unknown>[] = []
) => {
  const shellType = normalizeMainCardTypeKey(
    getMainCardType(card) || String(pages?.[0]?.type ?? "")
  );
  const endingImageUrl = String(card?.ending_image_url ?? "");
  const supportsEndingCover = shellType === "single_card";
  const hasEndingCoverPage = pages.some((page) => {
    const pageType = normalizeMainCardTypeKey(String(page?.type ?? ""));
    const content = (page?.content ?? {}) as Record<string, unknown>;
    const pageImageUrl =
      content.imageUrl || (Array.isArray(content.url) ? content.url[0] : content.url) || "";
    return pageType === "cover_image" && pageImageUrl === endingImageUrl;
  });
  if (!supportsEndingCover || !endingImageUrl || hasEndingCoverPage) {
    return pages;
  }
  return [
    ...pages,
    {
      order: pages.length + 1,
      type: "cover_image",
      content: { imageUrl: endingImageUrl, url: endingImageUrl },
      assets: [],
    },
  ];
};

export const mapMainCardResponseToPublishedCard = (
  rawCard: Record<string, unknown>,
  options?: {
    titleLabel?: string;
    appendEndingCover?: boolean;
    /** Talent-owned preview — show ending cover even when purchase flag is false. */
    isTalentView?: boolean;
  }
): PublishedCard => {
  const purchased = isMainCardPurchased(rawCard);
  const primary = getMainCardContent(rawCard);
  const type = getMainCardType(rawCard);
  const pages = resolveMainCardShellPages(rawCard, { isPurchased: purchased });
  const titleFallback = String(primary.title || primary.description || "");
  const shouldAppendEndingCover =
    Boolean(options?.appendEndingCover) && (purchased || Boolean(options?.isTalentView));
  const resolvedPages = shouldAppendEndingCover
    ? appendSingleCardEndingCoverPage(rawCard, pages)
    : pages;

  const talent = rawCard.talent as PublishedCard["talent"] | undefined;

  return {
    id: String(rawCard.id),
    title: talent?.username
      ? `${talent.username}'s ${options?.titleLabel ?? "Card"}`
      : titleFallback || options?.titleLabel || "Card",
    coverImageUrl: String(rawCard.cover_image_url ?? primary.thumbnail ?? ""),
    endingImageUrl: String(rawCard.ending_image_url ?? ""),
    template: String(rawCard.template ?? ""),
    card_status: String(rawCard.card_status ?? ""),
    deck_status: rawCard.deck_status,
    price: rawCard.price,
    seal_time:
      (rawCard.seal_time as PublishedCard["seal_time"]) ??
      (primary.seal_time as PublishedCard["seal_time"]) ??
      null,
    type: "Custom",
    createdAt: String(rawCard.created_at ?? ""),
    collector_count:
      Number.isFinite(Number(rawCard.collector_count)) && Number(rawCard.collector_count) >= 0
        ? Number(rawCard.collector_count)
        : undefined,
    clicked_count:
      Number.isFinite(Number(rawCard.clicked_count)) && Number(rawCard.clicked_count) >= 0
        ? Number(rawCard.clicked_count)
        : undefined,
    viewed_count:
      Number.isFinite(Number(rawCard.viewed_count)) && Number(rawCard.viewed_count) >= 0
        ? Number(rawCard.viewed_count)
        : undefined,
    pages: resolvedPages as PublishedCard["pages"],
    talent,
    talent_id: rawCard.talent_id as PublishedCard["talent_id"],
    partner_id: rawCard.partner_id as PublishedCard["partner_id"],
    partner: (rawCard.partner as PublishedCard["partner"]) ?? null,
    content: rawCard.content ?? primary,
    ...(rawCard.purchase != null ? { purchase: rawCard.purchase } : {}),
    ...withTableOfContents(rawCard),
  } as PublishedCard;
};

/** Talent book-view: page type injected for adding experiences. */
export const TALENT_EXP_CREATION_PAGE_TYPE: Partial<Record<DeckCardType, string>> = {
  Single_Card: "single_card_exp_creation",
  Demo_Card: "demo_card_exp_creation",
  Listening_Together_Card: "listening_together_exp_creation",
  Season_Card: "season_card_exp_creation",
};

const isTalentCreationPageType = (pageType: string, creationType: string) => {
  const key = normalizeMainCardTypeKey(pageType);
  if (key === normalizeMainCardTypeKey(creationType)) return true;
  // Season creation aliases that also map to SeasonCardExpCreation
  if (creationType === "season_card_exp_creation") {
    return key === "season_card_normal_exps";
  }
  return false;
};

export const injectTalentExpCreationPage = (
  card: PublishedCard | null,
  deckCardType: DeckCardType = "Single_Card"
): PublishedCard | null => {
  if (!card) return null;
  const creationType = TALENT_EXP_CREATION_PAGE_TYPE[deckCardType];
  if (!creationType) return card;

  const pages = Array.isArray(card.pages) ? card.pages : [];
  if (pages.length === 0) return card;

  const sealDeadlineMs = parseSealDeadline(card.seal_time);
  const sealed =
    String(card.card_status ?? "").toLowerCase() === "sealed" ||
    (sealDeadlineMs != null && sealDeadlineMs <= Date.now());

  // When sealed: strip creation pages entirely (do not leave a blank book page).
  if (sealed) {
    const withoutCreation = pages.filter(
      (page) =>
        !isTalentCreationPageType(String((page as { type?: string })?.type ?? ""), creationType)
    );
    if (withoutCreation.length === pages.length) return card;
    return { ...card, pages: withoutCreation as PublishedCard["pages"] };
  }

  const alreadyHasCreation = pages.some((page) =>
    isTalentCreationPageType(String((page as { type?: string })?.type ?? ""), creationType)
  );
  if (alreadyHasCreation) return card;

  const lastIdx = pages.length - 1;
  const lastPage = pages[lastIdx] as Record<string, unknown>;
  const lastOrder = Number(lastPage?.order ?? lastIdx + 1);

  const creationPage = {
    order: lastOrder + 1,
    type: creationType,
    content: { ...card },
    assets: [] as number[],
  };

  const nextPages = [...pages, creationPage];

  return {
    ...card,
    pages: nextPages,
  } as PublishedCard;
};

/** @deprecated Prefer injectTalentExpCreationPage(card, "Single_Card") */
export const injectSingleCardCreationPage = (card: PublishedCard | null): PublishedCard | null =>
  injectTalentExpCreationPage(card, "Single_Card");

export type OwnedDeckCardPurchaseFilter = "purchased" | "all" | "available";

export const readOwnedDeckCardPurchased = (
  card: PublishedCard,
  record?: DeckCardThumbnailRecord | null,
  _deckCardType?: DeckCardType
): boolean => {
  const purchasedFromRecord = record != null ? isCardThumbnailPurchased(record) : undefined;
  return Boolean(
    isPublishedCardPurchased(card) ||
    purchasedFromRecord ||
    card.is_listening_together_card_purchased
  );
};

const KAKAO_KIND_TO_DECK_TYPE: Record<DeckCardKakaoKind, DeckCardType> = {
  single: "Single_Card",
  demo: "Demo_Card",
  season: "Season_Card",
  collection: "Collection_Card",
  impact: "Impact_Card",
};

/** Whether the viewer has purchased the card (for expand back-face activity access). */
export const resolveDeckCardExpandPurchased = (
  card: PublishedCard | null | undefined,
  record?: DeckCardThumbnailRecord | null,
  shareKind?: DeckCardKakaoKind
): boolean => {
  if (!card) return true;

  if (shareKind) {
    return readOwnedDeckCardPurchased(card, record, KAKAO_KIND_TO_DECK_TYPE[shareKind]);
  }

  const purchased = card.purchase != null && Number((card.purchase as { id?: number })?.id) > 0;

  return Boolean(purchased);
};

export type OwnedDeckCardNavigationConfig = {
  getListPath: (card: PublishedCard, isTalentView: boolean) => string;
  getCollagePath?: (card: PublishedCard, isTalentView: boolean) => string;
};

export const SINGLE_CARD_NAVIGATION: OwnedDeckCardNavigationConfig = {
  getListPath: (card) => `/single-card/${card.id}`,
  getCollagePath: (card, isTalentView) =>
    isTalentView ? `/single-card/${card.id}/collage/talent` : `/single-card/${card.id}/collage`,
};

/** Demo card id lives on `content.id` / `ref_id`, not the main-card id. */
export const getDemoCardIdFromCard = (card?: PublishedCard | null): string | undefined => {
  if (!card) return undefined;

  const meta = card as PublishedCard & {
    demo_card_id?: number;
    content?: Record<string, unknown>;
  };
  const content = getPublishedCardShellContent(card);
  const id = content.id ?? content.demo_card_id ?? content.demoCardId ?? meta.demo_card_id;
  return id !== undefined && id !== null ? String(id) : undefined;
};

export const DEMO_CARD_NAVIGATION: OwnedDeckCardNavigationConfig = {
  getListPath: (card) => {
    const demoId = getDemoCardIdFromCard(card);
    return demoId ? `/demo-card/${card.id}?demoCardId=${demoId}` : `/demo-card/${card.id}`;
  },
};

/** Season card id lives on `content.id` / `ref_id`, not the main-card id. */
export const getSeasonCardIdFromCard = (card?: PublishedCard | null): string | undefined => {
  if (!card) return undefined;

  const meta = card as PublishedCard & {
    season_card_id?: number;
    ref_id?: number;
    content?: Record<string, unknown>;
  };
  const content = getPublishedCardShellContent(card);
  const id =
    content.id ??
    content.season_card_id ??
    content.seasonCardId ??
    meta.season_card_id ??
    meta.ref_id;
  return id !== undefined && id !== null ? String(id) : undefined;
};

export const SEASON_CARD_NAVIGATION: OwnedDeckCardNavigationConfig = {
  getListPath: (card) => {
    const seasonId = getSeasonCardIdFromCard(card);
    return seasonId
      ? `/season-card/${card.id}?seasonCardId=${seasonId}`
      : `/season-card/${card.id}`;
  },
};

export const mapDemoMainCardResponseToPublishedCard = (
  rawCard: Record<string, unknown>,
  options?: { titleLabel?: string; isTalentView?: boolean }
): PublishedCard => {
  const purchased = isMainCardPurchased(rawCard);
  const primary = getMainCardContent(rawCard);
  const refId = getMainCardRefId(rawCard);
  const pages = resolveMainCardShellPages(rawCard, { isPurchased: purchased });
  const titleFallback = String(primary.title || primary.description || "");
  const talent = rawCard.talent as PublishedCard["talent"] | undefined;
  const label = options?.titleLabel ?? "Demo Card";
  const demoCardId = Number(primary.id ?? refId);
  const embeddedExps = Array.isArray(primary.exps)
    ? (primary.exps as Record<string, unknown>[])
    : Array.isArray(rawCard.exps)
      ? (rawCard.exps as Record<string, unknown>[])
      : [];

  const published = {
    id: String(rawCard.id),
    title: talent?.username ? `${talent.username} • ${label}` : titleFallback || label,
    coverImageUrl: String(rawCard.cover_image_url ?? primary.thumbnail ?? ""),
    endingImageUrl: String(rawCard.ending_image_url ?? ""),
    template: String(rawCard.template ?? ""),
    card_status: String(rawCard.card_status ?? ""),
    deck_status: rawCard.deck_status,
    price: rawCard.price,
    type: "Custom",
    createdAt: String(rawCard.created_at ?? ""),
    pages: pages as PublishedCard["pages"],
    talent,
    ...(rawCard.purchase != null ? { purchase: rawCard.purchase } : {}),
    demo_card_id: Number.isFinite(demoCardId) && demoCardId > 0 ? demoCardId : undefined,
    ...(isObject(rawCard.content) ? { content: rawCard.content } : {}),
    ...withTableOfContents(rawCard),
  } as PublishedCard;

  return (
    enhanceDemoCardForPreview(published, {
      exps: embeddedExps,
      // Talent owns the card — show all episode pages (incl. unreleased) even when purchase is null.
      includeEpisodePages: Boolean(options?.isTalentView),
      isTalentView: Boolean(options?.isTalentView),
    }) ?? published
  );
};

export const mapSeasonMainCardResponseToPublishedCard = (
  rawCard: Record<string, unknown>,
  options?: { titleLabel?: string; isTalentView?: boolean }
): PublishedCard => {
  const purchased = isMainCardPurchased(rawCard);
  const primary = getMainCardContent(rawCard);
  const refId = getMainCardRefId(rawCard);
  const pages = resolveMainCardShellPages(rawCard, { isPurchased: purchased });
  const titleFallback = String(primary.title || primary.description || "");
  const talent = rawCard.talent as PublishedCard["talent"] | undefined;
  const label = options?.titleLabel ?? "Season Card";
  const seasonCardId = Number(primary.id ?? refId);
  const embeddedExps = Array.isArray(primary.exps)
    ? (primary.exps as Record<string, unknown>[])
    : Array.isArray(rawCard.exps)
      ? (rawCard.exps as Record<string, unknown>[])
      : [];

  const published = {
    id: String(rawCard.id),
    title: talent?.username ? `${talent.username} • ${label}` : titleFallback || label,
    coverImageUrl: String(rawCard.cover_image_url ?? primary.thumbnail ?? ""),
    endingImageUrl: String(rawCard.ending_image_url ?? ""),
    template: String(rawCard.template ?? ""),
    card_status: String(rawCard.card_status ?? ""),
    deck_status: rawCard.deck_status,
    price: rawCard.price,
    type: "Custom",
    seal_time: String(rawCard.seal_time ?? ""),
    createdAt: String(rawCard.created_at ?? ""),
    pages: pages as PublishedCard["pages"],
    talent,
    ...(rawCard.purchase != null ? { purchase: rawCard.purchase } : {}),
    ...(isObject(rawCard.content) ? { content: rawCard.content } : {}),
    ...(Number.isFinite(seasonCardId) && seasonCardId > 0 ? { season_card_id: seasonCardId } : {}),
    ...withTableOfContents(rawCard),
  } as PublishedCard;

  return (
    enhanceSeasonCardForPreview(published, {
      exps: embeddedExps,
      includeEpisodePages: Boolean(options?.isTalentView),
      isTalentView: Boolean(options?.isTalentView),
    }) ?? published
  );
};

export const LISTENING_TOGETHER_CARD_NAVIGATION: OwnedDeckCardNavigationConfig = {
  getListPath: (card) => `/listening-together-card/${card.id}`,
};

export const mapListeningTogetherMainCardResponseToPublishedCard = (
  rawCard: Record<string, unknown>,
  options?: { titleLabel?: string }
): PublishedCard => {
  const purchased = isMainCardPurchased(rawCard);
  const primary = getMainCardContent(rawCard);
  const pages = resolveMainCardShellPages(rawCard, { isPurchased: purchased });
  const titleFallback = String(primary.title || primary.description || "");
  const talent = rawCard.talent as PublishedCard["talent"] | undefined;
  const label = options?.titleLabel ?? "Listening Together Card";

  return {
    id: String(rawCard.id),
    title: talent?.username ? `${talent.username}'s ${label}` : titleFallback || label,
    coverImageUrl: String(rawCard.cover_image_url ?? primary.thumbnail ?? ""),
    template: String(rawCard.template ?? ""),
    type: "Custom",

    createdAt: String(rawCard.created_at ?? ""),
    pages: pages as PublishedCard["pages"],
    talent,
    is_listening_together_card_purchased: Boolean(
      rawCard.is_listening_together_card_purchased ?? primary.is_purchased ?? purchased
    ),
  };
};

export const readCardTalentId = (card: PublishedCard): number | null => {
  const id = Number((card as { talent_id?: number }).talent_id ?? card.talent?.id);
  return Number.isFinite(id) && id > 0 ? id : null;
};

export const enrichSeasonThumbnailCard = (
  card: PublishedCard,
  record?: DeckCardThumbnailRecord | null,
  purchasedMainCardIds?: Set<string>
): PublishedCard => {
  const pages = Array.isArray(card.pages) ? [...card.pages] : [];
  if (pages[0]) {
    const shellContent = {
      ...(pages[0].content ?? {}),
      card_kind: "season_card",
    };
    pages[0] = {
      ...pages[0],
      type: pages[0].type ?? "Season_Card",
      content: shellContent,
    };
  }

  const purchasedFromSet = purchasedMainCardIds?.has(String(card.id));
  const isPurchased = Boolean(
    purchasedFromSet ||
    pages[0]?.content?.is_purchased ||
    card.purchase != null ||
    (record != null && isCardThumbnailPurchased(record))
  );
  if (pages[0]) {
    pages[0] = {
      ...pages[0],
      content: { ...(pages[0].content ?? {}), is_purchased: isPurchased },
    };
  }
  return {
    ...card,
    pages,
    ...(isPurchased ? { purchase: card.purchase ?? {} } : {}),
  };
};
